import React, { createContext, useState, useCallback, useRef, useEffect } from "react";
import api from "../../services/api";
import { setBackendOffline, subscribeBackendStatus } from "../../helpers/backendStatus";

// Enquanto a API está fora do ar, confere de tempos em tempos se voltou
// (o socket pode nem ter caído, então não dá para esperar o "connected" dele).
const HTTP_PROBE_INTERVAL = 15000;

const ConnectionContext = createContext();

const CONNECTION_STATUS = {
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  RECONNECTING: "reconnecting",
};

const ConnectionProvider = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState(
    CONNECTION_STATUS.CONNECTED
  );
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [lastDisconnectedAt, setLastDisconnectedAt] = useState(null);
  const [lastConnectedAt, setLastConnectedAt] = useState(null);
  const [connectionError, setConnectionError] = useState(null);

  const [httpOfflineAt, setHttpOfflineAt] = useState(null);

  const prevStatusRef = useRef(CONNECTION_STATUS.CONNECTED);

  useEffect(
    () =>
      subscribeBackendStatus((offline) => {
        setHttpOfflineAt(offline ? Date.now() : null);
      }),
    []
  );

  useEffect(() => {
    if (!httpOfflineAt) return undefined;
    const timer = setInterval(() => {
      api.get("/version").catch(() => {});
    }, HTTP_PROBE_INTERVAL);
    return () => clearInterval(timer);
  }, [httpOfflineAt]);

  const onDisconnected = useCallback((reason) => {
    setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    setLastDisconnectedAt(Date.now());
    setConnectionError(reason || null);
    prevStatusRef.current = CONNECTION_STATUS.DISCONNECTED;
  }, []);

  const onReconnecting = useCallback((attemptNumber) => {
    setConnectionStatus(CONNECTION_STATUS.RECONNECTING);
    setReconnectAttempts(attemptNumber);
    prevStatusRef.current = CONNECTION_STATUS.RECONNECTING;
  }, []);

  const onConnected = useCallback(() => {
    const wasDisconnected =
      prevStatusRef.current !== CONNECTION_STATUS.CONNECTED;

    setConnectionStatus(CONNECTION_STATUS.CONNECTED);
    setLastConnectedAt(Date.now());
    setReconnectAttempts(0);
    setConnectionError(null);
    prevStatusRef.current = CONNECTION_STATUS.CONNECTED;
    // Socket conectou: o backend voltou
    setBackendOffline(false);

    return wasDisconnected;
  }, []);

  const onReconnectError = useCallback((error) => {
    setConnectionError(error?.message || "Erro de reconexão");
  }, []);

  // Queda vista pelo socket ou pelas chamadas HTTP — a sidebar mostra as duas
  const socketDown = connectionStatus !== CONNECTION_STATUS.CONNECTED;
  const isOffline = socketDown || Boolean(httpOfflineAt);
  const offlineSince = socketDown ? lastDisconnectedAt : httpOfflineAt;

  const value = {
    isOffline,
    offlineSince,
    connectionStatus,
    reconnectAttempts,
    lastDisconnectedAt,
    lastConnectedAt,
    connectionError,
    isConnected: connectionStatus === CONNECTION_STATUS.CONNECTED,
    isDisconnected: connectionStatus === CONNECTION_STATUS.DISCONNECTED,
    isReconnecting: connectionStatus === CONNECTION_STATUS.RECONNECTING,
    onDisconnected,
    onReconnecting,
    onConnected,
    onReconnectError,
  };

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  );
};

export { ConnectionContext, ConnectionProvider, CONNECTION_STATUS };
export default ConnectionContext;
